import {
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  EditRounded,
  TuneRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowField,
  listIndustrialWorkflowFields,
  updateIndustrialWorkflowField,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowFieldDefinition,
  IndustrialWorkflowFieldDefinitionUpsertPayload,
  IndustrialWorkflowFieldType,
} from '../types/industrialWorkflows';

type IndustrialWorkflowsFieldsConfigSectionProps = {
  processDefinitionId: string;
};

type FieldDisplayTier =
  | 'primary'
  | 'secondary'
  | 'technical';

type FieldFormState = {
  code: string;
  label: string;
  description: string;

  field_type: IndustrialWorkflowFieldType;

  section_key: string;
  display_order: string;

  is_required_on_create: boolean;
  is_active: boolean;

  visible_in_card: boolean;
  visible_in_list: boolean;
  visible_in_detail: boolean;

  display_tier: FieldDisplayTier;

  options_text: string;
  settings_text: string;
};

const EMPTY_FIELD_FORM: FieldFormState = {
  code: '',
  label: '',
  description: '',

  field_type: 'text',

  section_key: 'general',
  display_order: '10',

  is_required_on_create: false,
  is_active: true,

  visible_in_card: false,
  visible_in_list: true,
  visible_in_detail: true,

  display_tier: 'secondary',

  options_text: '',
  settings_text: '{}',
};

const FIELD_TYPES: {
  value: IndustrialWorkflowFieldType;
  label: string;
}[] = [
  {
    value: 'text',
    label: 'Texto corto',
  },
  {
    value: 'long_text',
    label: 'Texto largo',
  },
  {
    value: 'integer',
    label: 'Número entero',
  },
  {
    value: 'number',
    label: 'Número decimal',
  },
  {
    value: 'currency',
    label: 'Importe',
  },
  {
    value: 'boolean',
    label: 'Sí / no',
  },
  {
    value: 'date',
    label: 'Fecha',
  },
  {
    value: 'datetime',
    label: 'Fecha y hora',
  },
  {
    value: 'select',
    label: 'Lista de opciones',
  },
  {
    value: 'multi_select',
    label: 'Selección múltiple',
  },
  {
    value: 'email',
    label: 'Correo electrónico',
  },
  {
    value: 'url',
    label: 'Enlace',
  },
];

function parseInteger(
  value: string,
  fallback = 0,
) {
  const parsedValue = Number.parseInt(
    value,
    10,
  );

  return Number.isFinite(parsedValue)
    ? parsedValue
    : fallback;
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function parseOptions(value: string) {
  return Array.from(
    new Set(
      value
        .split('\n')
        .map(
          (option) =>
            option.trim(),
        )
        .filter(Boolean),
    ),
  );
}

function parseSettings(value: string) {
  const normalizedValue =
    value.trim();

  if (!normalizedValue) {
    return {};
  }

  const parsedValue =
    JSON.parse(normalizedValue);

  if (
    typeof parsedValue !== 'object'
    || parsedValue === null
    || Array.isArray(parsedValue)
  ) {
    throw new Error(
      'La configuración adicional debe ser un objeto JSON.',
    );
  }

  return parsedValue as Record<
    string,
    unknown
  >;
}

function getFieldDisplayTier(
  field: IndustrialWorkflowFieldDefinition,
): FieldDisplayTier {
  const displayTier =
    field.settings?.display_tier;

  if (
    displayTier === 'primary'
    || displayTier === 'secondary'
    || displayTier === 'technical'
  ) {
    return displayTier;
  }

  return field.visible_in_card
    ? 'primary'
    : 'secondary';
}

function getFieldDisplayTierLabel(
  displayTier: FieldDisplayTier,
) {
  if (displayTier === 'primary') {
    return 'Principal';
  }

  if (displayTier === 'technical') {
    return 'Técnico';
  }

  return 'Secundario';
}

function normalizeCode(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(
      /[\u0300-\u036f]/g,
      '',
    )
    .replace(
      /[^a-zA-Z0-9]+/g,
      '_',
    )
    .replace(
      /^_+|_+$/g,
      '',
    )
    .toUpperCase();
}

function buildUniqueFieldCode(
  label: string,
  fields:
    IndustrialWorkflowFieldDefinition[],
) {
  const normalizedBaseCode =
    normalizeCode(label)
    || 'CAMPO';

  const existingCodes =
    new Set(
      fields.map(
        (field) =>
          field.code
            .trim()
            .toUpperCase(),
      ),
    );

  if (
    !existingCodes.has(
      normalizedBaseCode,
    )
  ) {
    return normalizedBaseCode;
  }

  let suffix = 2;

  while (
    existingCodes.has(
      `${normalizedBaseCode}_${suffix}`,
    )
  ) {
    suffix += 1;
  }

  return `${normalizedBaseCode}_${suffix}`;
}

function getNextDisplayOrder(
  fields:
    IndustrialWorkflowFieldDefinition[],
) {
  if (!fields.length) {
    return 10;
  }

  return (
    Math.max(
      ...fields.map(
        (field) =>
          field.display_order,
      ),
    )
    + 10
  );
}

function getFieldTypeLabel(
  fieldType:
    IndustrialWorkflowFieldType,
) {
  return (
    FIELD_TYPES.find(
      (option) =>
        option.value
        === fieldType,
    )?.label
    ?? fieldType
  );
}

function buildFieldPayload(
  form: FieldFormState,
): IndustrialWorkflowFieldDefinitionUpsertPayload {
  const fieldUsesOptions =
    form.field_type === 'select'
    || form.field_type
      === 'multi_select';

  const parsedSettings =
    parseSettings(
      form.settings_text,
    );

  return {
    code: form.code.trim(),
    label: form.label.trim(),
    description:
      form.description.trim()
      || null,

    field_type: form.field_type,

    section_key:
      form.section_key.trim()
      || 'general',

    display_order:
      parseInteger(
        form.display_order,
        10,
      ),

    is_required_on_create:
      form.is_required_on_create,

    is_active:
      form.is_active,

    visible_in_card:
      form.display_tier === 'primary'
        ? true
        : form.visible_in_card,

    visible_in_list:
      form.visible_in_list,

    visible_in_detail:
      true,

    options: fieldUsesOptions
      ? parseOptions(
          form.options_text,
        )
      : [],

    settings: {
      ...parsedSettings,
      display_tier:
        form.display_tier,
    },
  };
}

export default function IndustrialWorkflowsFieldsConfigSection({
  processDefinitionId,
}: IndustrialWorkflowsFieldsConfigSectionProps) {
  const queryClient =
    useQueryClient();

  const { enqueueSnackbar } =
    useSnackbar();

  const [
    dialogOpen,
    setDialogOpen,
  ] =
    useState(false);

  const [
    editingField,
    setEditingField,
  ] =
    useState<
      IndustrialWorkflowFieldDefinition
      | null
    >(null);

  const [
    fieldForm,
    setFieldForm,
  ] =
    useState<FieldFormState>(
      EMPTY_FIELD_FORM,
    );

  const fieldsQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-fields',
        processDefinitionId,
        true,
      ],
      queryFn: () =>
        listIndustrialWorkflowFields(
          processDefinitionId,
          true,
        ),
    });

  const orderedFields =
    useMemo(
      () =>
        [
          ...(fieldsQuery.data
            ?? []),
        ].sort(
          (
            left,
            right,
          ) =>
            left.display_order
            - right.display_order
            || left.label.localeCompare(
              right.label,
            ),
        ),
      [
        fieldsQuery.data,
      ],
    );

  const createFieldMutation =
    useMutation({
      mutationFn: (
        payload:
          IndustrialWorkflowFieldDefinitionUpsertPayload,
      ) =>
        createIndustrialWorkflowField(
          processDefinitionId,
          payload,
        ),
      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              'industrial-workflows-fields',
              processDefinitionId,
            ],
          });

        setDialogOpen(false);
        setEditingField(null);
        setFieldForm(
          EMPTY_FIELD_FORM,
        );

        enqueueSnackbar(
          'Campo creado correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const updateFieldMutation =
    useMutation({
      mutationFn: ({
        fieldDefinitionId,
        payload,
      }: {
        fieldDefinitionId: string;
        payload:
          IndustrialWorkflowFieldDefinitionUpsertPayload;
      }) =>
        updateIndustrialWorkflowField(
          processDefinitionId,
          fieldDefinitionId,
          payload,
        ),
      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              'industrial-workflows-fields',
              processDefinitionId,
            ],
          });

        setDialogOpen(false);
        setEditingField(null);
        setFieldForm(
          EMPTY_FIELD_FORM,
        );

        enqueueSnackbar(
          'Campo actualizado correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const mutationIsPending =
    createFieldMutation.isPending
    || updateFieldMutation.isPending;

  const handleCloseDialog = () => {
    if (mutationIsPending) {
      return;
    }

    setDialogOpen(false);
    setEditingField(null);
    setFieldForm(
      EMPTY_FIELD_FORM,
    );
  };

  const handleCreateField = () => {
    setEditingField(null);

    setFieldForm({
      ...EMPTY_FIELD_FORM,
      display_order: String(
        getNextDisplayOrder(
          orderedFields,
        ),
      ),
    });

    setDialogOpen(true);
  };

  const handleEditField = (
    field:
      IndustrialWorkflowFieldDefinition,
  ) => {
    setEditingField(field);

    setFieldForm({
      code: field.code,
      label: field.label,
      description:
        field.description
        ?? '',

      field_type:
        field.field_type,

      section_key:
        field.section_key,

      display_order:
        String(
          field.display_order,
        ),

      is_required_on_create:
        field.is_required_on_create,

      is_active:
        field.is_active,

      visible_in_card:
        field.visible_in_card,

      visible_in_list:
        field.visible_in_list,

      visible_in_detail:
        field.visible_in_detail,

      display_tier:
        getFieldDisplayTier(field),

      options_text:
        field.options
          .map(
            (option) =>
              typeof option
              === 'string'
                ? option
                : JSON.stringify(
                    option,
                  ),
          )
          .join('\n'),

      settings_text:
        JSON.stringify(
          field.settings
          ?? {},
          null,
          2,
        ),
    });

    setDialogOpen(true);
  };

  const handleSaveField = () => {
    const normalizedLabel =
      fieldForm.label.trim();

    if (!normalizedLabel) {
      enqueueSnackbar(
        'Indica el nombre del campo.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const fieldUsesOptions =
      fieldForm.field_type
      === 'select'
      || fieldForm.field_type
        === 'multi_select';

    if (
      fieldUsesOptions
      && !parseOptions(
        fieldForm.options_text,
      ).length
    ) {
      enqueueSnackbar(
        'Introduce al menos una opción disponible.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    try {
      const normalizedForm:
        FieldFormState =
        editingField
          ? {
              ...fieldForm,
              label:
                normalizedLabel,
            }
          : {
              ...fieldForm,
              code:
                buildUniqueFieldCode(
                  normalizedLabel,
                  orderedFields,
                ),
              label:
                normalizedLabel,
            };

      const payload =
        buildFieldPayload(
          normalizedForm,
        );

      if (editingField) {
        updateFieldMutation
          .mutate({
            fieldDefinitionId:
              editingField.id,
            payload,
          });

        return;
      }

      createFieldMutation
        .mutate(payload);
    } catch (error) {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          md: 'center',
        }}
        spacing={1.25}
      >
        <Stack spacing={0.35}>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <TuneRounded
              sx={{
                color: '#38bdf8',
              }}
            />

            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize:
                  '1.02rem',
              }}
            >
              Campos del expediente
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: '#94a3b8',
              fontSize:
                '0.84rem',
            }}
          >
            Define qué información adicional
            deseas registrar en los expedientes
            de este flujo.
          </Typography>
        </Stack>

        <Button
          variant="outlined"
          startIcon={
            <AddRounded />
          }
          onClick={
            handleCreateField
          }
          sx={{
            fontWeight: 900,
            textTransform:
              'none',
          }}
        >
          Nuevo campo
        </Button>
      </Stack>

      <Alert
        severity="info"
        variant="outlined"
        sx={{
          bgcolor:
            'rgba(14,116,144,0.08)',
        }}
      >
        Los campos activos aparecerán
        automáticamente al crear y editar
        expedientes. Clasifícalos como
        principales, secundarios o técnicos
        para controlar su protagonismo visual.
      </Alert>

      {fieldsQuery.isLoading ? (
        <Box sx={{ py: 2 }}>
          <Typography
            sx={{
              color: '#94a3b8',
            }}
          >
            Cargando campos...
          </Typography>
        </Box>
      ) : null}

      {fieldsQuery.isError ? (
        <Alert
          severity="error"
          variant="outlined"
        >
          {getErrorMessage(
            fieldsQuery.error,
          )}
        </Alert>
      ) : null}

      {!fieldsQuery.isLoading
      && !fieldsQuery.isError
      && !orderedFields.length ? (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            bgcolor:
              'rgba(14,116,144,0.08)',
          }}
        >
          Todavía no hay campos configurados.
          Añade el primero para adaptar los
          expedientes a tu proceso.
        </Alert>
      ) : null}

      <Stack spacing={1}>
        {orderedFields.map(
          (field) => (
            <Paper
              key={field.id}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#94a3b8',
                  0.16,
                )}`,
                bgcolor:
                  'rgba(15,23,42,0.32)',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
              >
                <TuneRounded
                  sx={{
                    color:
                      field.is_active
                        ? '#38bdf8'
                        : '#64748b',
                  }}
                />

                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.7}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography
                      sx={{
                        color:
                          '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      {field.label}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        getFieldTypeLabel(
                          field.field_type,
                        )
                      }
                      sx={{
                        color:
                          '#bae6fd',
                        bgcolor:
                          alpha(
                            '#0284c7',
                            0.16,
                          ),
                      }}
                    />

                    <Chip
                      size="small"
                      label={getFieldDisplayTierLabel(
                        getFieldDisplayTier(field),
                      )}
                      sx={{
                        color:
                          getFieldDisplayTier(field) === 'primary'
                            ? '#fde68a'
                            : getFieldDisplayTier(field) === 'technical'
                              ? '#c4b5fd'
                              : '#cbd5e1',
                        bgcolor:
                          getFieldDisplayTier(field) === 'primary'
                            ? alpha(
                                '#f59e0b',
                                0.15,
                              )
                            : getFieldDisplayTier(field) === 'technical'
                              ? alpha(
                                  '#8b5cf6',
                                  0.15,
                                )
                              : alpha(
                                  '#64748b',
                                  0.12,
                                ),
                      }}
                    />

                    <Chip
                      size="small"
                      label={
                        field
                          .is_required_on_create
                          ? 'Obligatorio'
                          : 'Opcional'
                      }
                      sx={{
                        color:
                          field
                            .is_required_on_create
                            ? '#fde68a'
                            : '#cbd5e1',
                        bgcolor:
                          field
                            .is_required_on_create
                            ? alpha(
                                '#f59e0b',
                                0.15,
                              )
                            : alpha(
                                '#64748b',
                                0.12,
                              ),
                      }}
                    />

                    {field
                      .visible_in_list ? (
                      <Chip
                        size="small"
                        label="Visible en listado"
                        sx={{
                          color:
                            '#bbf7d0',
                          bgcolor:
                            alpha(
                              '#22c55e',
                              0.14,
                            ),
                        }}
                      />
                    ) : null}

                    {!field.is_active ? (
                      <Chip
                        size="small"
                        label="Inactivo"
                        sx={{
                          color:
                            '#cbd5e1',
                          bgcolor:
                            alpha(
                              '#64748b',
                              0.14,
                            ),
                        }}
                      />
                    ) : null}
                  </Stack>

                  {field.description ? (
                    <Typography
                      sx={{
                        color:
                          '#94a3b8',
                        fontSize:
                          '0.8rem',
                        mt: 0.45,
                      }}
                    >
                      {field.description}
                    </Typography>
                  ) : null}
                </Box>

                <IconButton
                  size="small"
                  aria-label="Editar campo"
                  onClick={() =>
                    handleEditField(
                      field,
                    )
                  }
                  sx={{
                    color:
                      '#94a3b8',
                  }}
                >
                  <EditRounded
                    fontSize="small"
                  />
                </IconButton>
              </Stack>
            </Paper>
          ),
        )}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingField
            ? 'Editar campo del expediente'
            : 'Nuevo campo del expediente'}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <TextField
              label="Nombre del campo"
              value={
                fieldForm.label
              }
              onChange={(event) =>
                setFieldForm(
                  (current) => ({
                    ...current,
                    label:
                      event.target.value,
                  }),
                )
              }
              helperText="Este será el nombre que verán los usuarios al registrar un expediente."
              fullWidth
              required
            />

            <TextField
              label="Descripción o ayuda"
              value={
                fieldForm.description
              }
              onChange={(event) =>
                setFieldForm(
                  (current) => ({
                    ...current,
                    description:
                      event.target.value,
                  }),
                )
              }
              multiline
              minRows={2}
              helperText="Añade una explicación breve cuando facilite el registro correcto del dato."
              fullWidth
            />

            <TextField
              select
              label="Tipo de dato"
              value={
                fieldForm.field_type
              }
              onChange={(event) =>
                setFieldForm(
                  (current) => ({
                    ...current,
                    field_type:
                      event.target
                        .value as IndustrialWorkflowFieldType,
                  }),
                )
              }
              fullWidth
            >
              {FIELD_TYPES.map(
                (option) => (
                  <MenuItem
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {option.label}
                  </MenuItem>
                ),
              )}
            </TextField>

            <TextField
              select
              label="Protagonismo visual"
              value={
                fieldForm.display_tier
              }
              onChange={(event) =>
                setFieldForm(
                  (current) => ({
                    ...current,
                    display_tier:
                      event.target.value as FieldDisplayTier,
                    visible_in_card:
                      event.target.value === 'primary'
                        ? true
                        : current.visible_in_card,
                  }),
                )
              }
              helperText="Principal para datos clave como Cliente, RP o RD. Técnico para datos productivos como caja o metros por caja."
              fullWidth
            >
              <MenuItem value="primary">
                Principal
              </MenuItem>

              <MenuItem value="secondary">
                Secundario
              </MenuItem>

              <MenuItem value="technical">
                Técnico
              </MenuItem>
            </TextField>

            {fieldForm.field_type
              === 'select'
            || fieldForm.field_type
              === 'multi_select' ? (
              <TextField
                label="Opciones disponibles"
                value={
                  fieldForm.options_text
                }
                onChange={(event) =>
                  setFieldForm(
                    (current) => ({
                      ...current,
                      options_text:
                        event.target.value,
                    }),
                  )
                }
                multiline
                minRows={4}
                helperText="Introduce una opción por línea."
                fullWidth
                required
              />
            ) : null}

            <Stack spacing={0.15}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      fieldForm
                        .is_required_on_create
                    }
                    onChange={(event) =>
                      setFieldForm(
                        (current) => ({
                          ...current,
                          is_required_on_create:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label="Obligatorio al crear un expediente"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={
                      fieldForm
                        .visible_in_list
                    }
                    onChange={(event) =>
                      setFieldForm(
                        (current) => ({
                          ...current,
                          visible_in_list:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label="Mostrar también en el listado general"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={
                      fieldForm
                        .is_active
                    }
                    onChange={(event) =>
                      setFieldForm(
                        (current) => ({
                          ...current,
                          is_active:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label="Campo activo"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              handleCloseDialog
            }
            disabled={
              mutationIsPending
            }
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              mutationIsPending
              || !fieldForm
                .label
                .trim()
            }
            onClick={
              handleSaveField
            }
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}